import React, { useState, useEffect, useRef } from 'react';
import { Send, User, MessageCircle, Phone, ArrowLeft, Search, FileText, Filter, X } from 'lucide-react';
import { useChat } from '../context/ChatContext';

interface ChatViewProps {
    user: { email: string; role: string };
    userMetadata: Record<string, any> | null;
}

const ChatView: React.FC<ChatViewProps> = ({ user, userMetadata }) => {
    const { conversations, activeConvId, setActiveConvId, sendMessage, swapScript } = useChat();
    const [input, setInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    const [isSwapping, setIsSwapping] = useState(false);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const activeConv = conversations[activeConvId || 'default'];

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [activeConv?.messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim()) {return;}

        const convId = activeConvId || 'default';
        await sendMessage(convId, input, {
            userEmail: user.email,
            userRole: user.role,
            meta: userMetadata
        });
        setInput('');
    };

    const handleSwap = async () => {
        setIsSwapping(true);
        try {
            await swapScript(activeConvId, 'suggested-script-1', 'User requested optimization');
        } finally {
            setIsSwapping(false);
        }
    };

    return (
        <div className="flex h-[calc(100vh-180px)] bg-white/50 backdrop-blur-xl rounded-[2.5rem] overflow-hidden border border-white/20 shadow-xl relative">
            {/* Sidebar: Conversation List */}
            <div className="w-72 border-r border-slate-100 bg-white/40 flex flex-col">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">Conversations</h2>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {Object.values(conversations).map((conv: any) => (
                        <button
                            key={conv.id}
                            onClick={() => setActiveConvId(conv.id)}
                            className={`w-full p-4 rounded-2xl text-left transition-all ${activeConvId === conv.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-100'}`}
                        >
                            <div className="font-bold truncate">{conv.participants.join(', ')}</div>
                            <div className={`text-[10px] mt-1 ${activeConvId === conv.id ? 'text-blue-100' : 'text-slate-400'}`}>
                                {new Date(conv.lastMessageAt).toLocaleDateString()}
                            </div>
                        </button>
                    ))}
                </div>
                <div className="p-4 border-t border-slate-100">
                    <button
                        onClick={() => setIsLogModalOpen(true)}
                        className="w-full p-3 flex items-center justify-center gap-2 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-all text-sm font-bold"
                    >
                        <FileText size={16} />
                        View Full Logs
                    </button>
                </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col relative">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white/80">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-xl text-blue-700">
                            <MessageCircle size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-800 tracking-tight leading-none">
                                {activeConv ? `Conversation ${activeConv.id}` : 'Select a Chat'}
                            </h2>
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-1 block">Active Session</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleSwap}
                            disabled={!activeConvId || isSwapping}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2"
                        >
                            {isSwapping ? 'Swapping...' : 'Swap Script'}
                        </button>
                    </div>
                </div>

                <div
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto p-6 space-y-4 scroll-smooth"
                >
                    {!activeConv ? (
                        <div className="text-center py-12 text-slate-300">
                            <p className="text-sm font-medium">Please select a conversation from the sidebar to begin.</p>
                        </div>
                    ) : (
                        activeConv.messages.map((m, idx) => {
                            const isMe = m.sender === user.email;
                            return (
                                <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl shadow-sm text-sm ${isMe
                                            ? 'bg-blue-700 text-white rounded-tr-none'
                                            : 'bg-white border border-slate-100 text-slate-800 rounded-tl-none'
                                        }`}>
                                        <div className="mb-0.5 whitespace-pre-wrap">{m.content}</div>
                                        <div className={`text-[9px] opacity-60 mt-1 font-medium ${isMe ? 'text-blue-50' : 'text-slate-400'}`}>
                                            {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="p-4 bg-white/80 border-t border-slate-100">
                    <form onSubmit={handleSend} className="space-y-3">
                        <div className="relative">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Type your message..."
                                className="w-full bg-slate-50 border border-slate-200 rounded-2xl py-4 pl-6 pr-14 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all text-sm font-medium"
                            />
                            <button
                                type="submit"
                                className="absolute right-2 top-2 p-2.5 bg-blue-700 text-white rounded-xl hover:bg-blue-800 transition-all active:scale-95 shadow-lg shadow-blue-200"
                            >
                                <Send size={20} />
                            </button>
                        </div>
                    </form>
                </div>

                {/* Modal Log Viewer */}
                {isLogModalOpen && (
                    <div className="absolute inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6">
                        <div className="bg-white w-full max-w-4xl h-5/6 rounded-3xl shadow-2xl flex flex-col overflow-hidden">
                            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <FileText className="text-blue-600" />
                                    <h3 className="text-xl font-black text-slate-800">Comprehensive Chat Logs</h3>
                                </div>
                                <button onClick={() => setIsLogModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="p-6 flex gap-4 border-b border-slate-100 bg-slate-50/50">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search messages across all conversations..."
                                        className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    />
                                </div>
                                <button className="p-2 px-4 bg-white border border-slate-200 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-slate-50 transition-all">
                                    <Filter size={16} />
                                    Filter
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                <p className="text-slate-400 text-sm italic">Search results will appear here...</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatView;
